import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { generateProfilePictureSVG, generateGradientFromUsername } from '../utils/profilePicture'
import { isValidUrl } from '../utils/validation'
import './CreationDetail.css'

interface Creation {
  id: string
  title: string
  description: string
  authorId: string
  authorUsername: string
  category: string
  mediaUrl?: string
  createdAt: string
  likes: string[]
  about?: string
  techniques?: string[]
}

const CreationDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [creation, setCreation] = useState<Creation | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCreation = async () => {
      const { data, error } = await supabase
        .from('creations')
        .select('*, profiles!creations_author_id_fkey(username)')
        .eq('id', id)
        .single()

      if (error || !data) {
        setLoading(false)
        return
      }

      const { data: likesData } = await supabase
        .from('creation_likes')
        .select('user_id')
        .eq('creation_id', id)

      const profile = data.profiles as Record<string, unknown> | null
      const likes = (likesData || []).map((l: Record<string, unknown>) => l.user_id as string)

      setCreation({
        id: data.id,
        title: data.title,
        description: data.description || '',
        authorId: data.author_id,
        authorUsername: (profile?.username as string) || 'unknown',
        category: data.category || 'audio',
        mediaUrl: data.media_url || undefined,
        about: data.about || undefined,
        techniques: data.techniques || undefined,
        createdAt: data.created_at,
        likes,
      })
      setLoading(false)
    }

    if (id) fetchCreation()
  }, [id])

  const handleToggleLike = async () => {
    if (!user || !creation) return

    const hasLiked = creation.likes.includes(user.id)

    // Optimistic update
    setCreation(prev => {
      if (!prev) return prev
      const newLikes = hasLiked
        ? prev.likes.filter(uid => uid !== user.id)
        : [...prev.likes, user.id]
      return { ...prev, likes: newLikes }
    })

    if (hasLiked) {
      await supabase
        .from('creation_likes')
        .delete()
        .eq('creation_id', creation.id)
        .eq('user_id', user.id)
    } else {
      await supabase
        .from('creation_likes')
        .insert({ creation_id: creation.id, user_id: user.id })
    }
  }

  if (loading) {
    return (
      <main className="main-content creation-detail-page">
        <div className="creation-detail-container">
          <p>loading...</p>
        </div>
      </main>
    )
  }

  if (!creation) {
    return (
      <main className="main-content creation-detail-page">
        <div className="creation-detail-container">
          <h1>creation not found</h1>
          <Link to="/community?tab=creations" className="creation-back-link">
            back to creations
          </Link>
        </div>
      </main>
    )
  }

  const isLiked = user && creation.likes.includes(user.id)

  return (
    <main className="main-content creation-detail-page">
      <div className="creation-detail-container">
        <Link to="/community?tab=creations" className="creation-back-link">
          <i className="fas fa-arrow-left"></i>
          back to creations
        </Link>

        <div className="creation-detail-header">
          <div className="creation-detail-author">
            <div
              className="creation-detail-avatar"
              style={{ background: generateGradientFromUsername(creation.authorUsername) }}
              dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(creation.authorUsername, 48) }}
            />
            <div className="creation-detail-author-info">
              <div className="creation-detail-author-name">{creation.authorUsername}</div>
              <div className="creation-detail-date">
                {new Date(creation.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>
          </div>
          <div className="creation-detail-meta">
            <span className="creation-detail-category">{creation.category}</span>
            <button
              className={`creation-detail-like ${isLiked ? 'liked' : ''}`}
              onClick={handleToggleLike}
              disabled={!user}
            >
              <i className="fas fa-heart"></i>
              <span>{creation.likes.length}</span>
            </button>
          </div>
        </div>

        <h1 className="creation-detail-title">{creation.title}</h1>
        <p className="creation-detail-description">{creation.description}</p>

        {creation.mediaUrl && isValidUrl(creation.mediaUrl) && (
          <div className="creation-detail-media">
            <a
              href={creation.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="creation-detail-media-link"
            >
              <i className="fas fa-external-link-alt"></i>
              view creation
            </a>
          </div>
        )}

        <div className="creation-detail-content">
          {creation.about && (
            <div className="creation-detail-section">
              <h2>about this creation</h2>
              <p>{creation.about}</p>
            </div>
          )}

          {creation.techniques && creation.techniques.length > 0 && (
            <div className="creation-detail-section">
              <h2>techniques used</h2>
              <ul className="creation-detail-list">
                {creation.techniques.map((technique, index) => (
                  <li key={index}>{technique}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default CreationDetail
